import { Fragment, type ReactNode } from 'react'
import type { MemoActionConflict } from '@ai-meeting/shared'
import { ExclamationTriangleIcon, XMarkIcon } from '@/components/Icons'
import { fmt } from '@/constants/ui-locale'
import { useUiStrings } from '@/hooks/useUiStrings'
// In its own module, not beside the component: `react-refresh/only-export-components` is right
// that a file exporting both a component and a helper stops being hot-reloadable, and this is a
// pure function worth testing without rendering anything.
import { daysApart } from '@/utils/memoActionConflict'

/**
 * Render one localized sentence, swapping `{name}` placeholders for nodes.
 *
 * `fmt` covers this when every value is a string, and here three are not: two are emphasised and one
 * is a link. Splitting the finished sentence beats assembling it from translated fragments — word
 * order is not shared between the locales this app ships, and "but ticket X on Backlog" does not
 * survive being cut into pieces and reordered by the renderer.
 */
function interpolate(template: string, parts: Record<string, ReactNode>): ReactNode[] {
  return template.split(/(\{\w+\})/g).map((chunk, index) => {
    const name = chunk.length > 2 && chunk.startsWith('{') && chunk.endsWith('}')
      ? chunk.slice(1, -1)
      : null
    return (
      <Fragment key={index}>{name !== null && name in parts ? parts[name] : chunk}</Fragment>
    )
  })
}

interface ConfirmActionConflictAlertProps {
  conflict: MemoActionConflict
  onDismiss: () => void
}

/**
 * Warns that a deadline or status said in the meeting disagrees with the ticket (BRAI-549).
 *
 * Drawn as its own card under Confirm Action rather than beneath the item that produced it: the
 * disagreement is about a ticket, not about one line of the list, and a reader scanning a live
 * meeting should not have to find the right row first.
 *
 * The values are printed exactly as the server sent them. The design shows `30/06`, which is neither
 * locale this app ships — a Japanese reader expects the year first and an English one expects the
 * month — and a day/month rendering silently drops the year, which is the one part of a deadline
 * that makes "next June" different from "this June".
 */
export default function ConfirmActionConflictAlert({
  conflict,
  onDismiss,
}: ConfirmActionConflictAlertProps) {
  const { t } = useUiStrings()

  const fieldLabel =
    conflict.field === 'deadline' ? t.conflictAlertFieldDeadline : t.conflictAlertFieldStatus
  const gap = daysApart(conflict)

  const emphasis = 'font-semibold text-slate-900 dark:text-[var(--text-primary)]'

  return (
    <div
      className="mt-3 rounded-lg border border-amber-400/50 bg-amber-50 p-3 text-xs leading-5 dark:border-amber-500/40 dark:bg-amber-500/10"
      data-testid={`confirm-item-conflict-${conflict.issue_key}`}
    >
      <div className="flex items-start gap-2">
        <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-300" />

        {/* Body and button share the title's left edge rather than the card's, so the icon marks the
            whole warning instead of only its first line. */}
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-amber-800 dark:text-amber-300">
            {fmt(t.conflictAlertTitle, { field: fieldLabel })}
          </p>

          <p className="mt-1.5 text-slate-800 dark:text-[var(--text-secondary)]">
            {interpolate(t.conflictAlertBody, {
              field: fieldLabel,
              incoming: (
                <span className={emphasis}>{conflict.incoming ?? t.conflictValueUnset}</span>
              ),
              issueKey: (
                <span className="font-semibold text-blue-600 dark:text-[var(--accent)]">
                  {conflict.issue_key}
                </span>
              ),
              current: <span className={emphasis}>{conflict.current ?? t.conflictValueUnset}</span>,
            })}
            {gap !== null && ` ${fmt(t.conflictAlertDaysApart, { days: gap })}`}
          </p>

          <a
            href={conflict.link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2.5 inline-block rounded-md border border-slate-300 bg-white px-2.5 py-1 font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)] dark:text-[var(--text-secondary)] dark:hover:bg-[var(--bg-secondary)]"
          >
            {t.conflictAlertViewDetail}
          </a>
        </div>

        <button
          type="button"
          onClick={onDismiss}
          aria-label={t.conflictAlertDismiss}
          className="-mr-1 -mt-1 shrink-0 rounded p-1 text-amber-700/70 transition-colors hover:bg-amber-100 hover:text-amber-900 dark:text-amber-300/70 dark:hover:bg-amber-500/20 dark:hover:text-amber-200"
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
